import type { ItemUnit } from './index';

/**
 * Individual instance within a BatchDraftItem.
 * Each instance represents a separate batch with its own expiry date.
 */
export interface BatchDraftInstance {
  /** Client-side unique ID for this instance (nanoid) */
  instanceId: string;

  /** Quantity for this instance */
  quantity: number;

  /** Expiry date in YYYY-MM-DD format */
  expiryDate?: string;

  /** Purchase date in YYYY-MM-DD format */
  purchaseDate?: string;

  /** Instance-specific notes */
  notes?: string | null;

  /** Timestamp when this instance was added */
  addedAt: number;
}

/**
 * Draft item for batch entry before submission to API.
 * Stored locally until user confirms and submits the batch.
 */
export interface BatchDraftItem {
  /** Client-side unique ID for tracking (nanoid) */
  clientId: string;

  /** Product UUID if from local database */
  productId?: string;

  /** Barcode if from external source (needs materialization before submit) */
  barcode?: string;

  /** Custom name if manual entry (no productId or barcode) */
  customName?: string;

  /** Display name for UI (denormalized from product or customName) */
  displayName: string;

  /** Display brand for UI (from product) */
  displayBrand?: string;

  /** Display image URL for UI (from product) */
  displayImageUrl?: string;

  /**
   * Whether this item is from an external source (e.g., Open Food Facts).
   * External items need to be materialized via materializeExternalProduct before submission.
   */
  isExternal: boolean;

  /** Unit of measurement */
  unit: ItemUnit;

  /** Storage location UUID */
  storageLocationId?: string;

  /** Timestamp when this item was added to draft (for sorting) */
  addedAt: number;

  /**
   * Individual instances with their own expiry dates.
   * Each instance represents a separate batch of the same product.
   */
  instances: BatchDraftInstance[];

  // ---- Deprecated fields (kept for v1 compatibility) ----

  /**
   * @deprecated Use instances array instead. Kept for v1 compatibility.
   * Total quantity across all instances.
   */
  quantity: number;

  /**
   * @deprecated Use instances array instead. Kept for v1 compatibility.
   * Expiry date for the first/only instance.
   */
  expiryDate?: string;

  /**
   * @deprecated Use instances array instead. Kept for v1 compatibility.
   * Notes for the first/only instance.
   */
  notes?: string | null;

  /**
   * @deprecated Use instances array instead. Kept for v1 compatibility.
   * Purchase date for the first/only instance.
   */
  purchaseDate?: string;
}

/**
 * Serializable format for persisting batch draft to localStorage/AsyncStorage.
 * Includes expiration for auto-cleanup.
 */
export interface BatchDraftExport {
  /** Items in the draft batch */
  items: BatchDraftItem[];

  /** Default storage location ID for all items */
  defaultStorageLocationId: string | null;

  /** Unix timestamp when this draft expires (24h from creation) */
  expiresAt: number;

  /** Schema version for future migrations */
  version: 1 | 2;
}

/**
 * V1 BatchDraftItem for migration (without instances array).
 */
export interface BatchDraftItemV1 {
  clientId: string;
  productId?: string;
  barcode?: string;
  customName?: string;
  displayName: string;
  displayBrand?: string;
  displayImageUrl?: string;
  isExternal: boolean;
  quantity: number;
  unit: ItemUnit;
  storageLocationId?: string;
  expiryDate?: string;
  notes?: string;
  addedAt: number;
}

/**
 * V1 export format for migration.
 */
export interface BatchDraftExportV1 {
  items: BatchDraftItemV1[];
  defaultStorageLocationId: string | null;
  expiresAt: number;
  version: 1;
}

/**
 * Validation status for a draft item.
 * Used to show inline validation errors in the UI.
 */
export type BatchDraftItemStatus = 'valid' | 'needs_input' | 'error';

export { EXPIRY_PRESETS, type ExpiryPreset, calculateExpiryDate } from './presets';

/**
 * Helper to check if a draft item is valid for submission.
 * @param item - The draft item to validate
 * @returns Status indicating if the item can be submitted
 */
export function validateBatchDraftItem(item: BatchDraftItem): BatchDraftItemStatus {
  // Must have either productId, barcode, or customName
  if (!item.productId && !item.barcode && !item.customName) {
    return 'needs_input';
  }

  // displayName is required for UI
  if (!item.displayName) {
    return 'needs_input';
  }

  // Quantity must be positive (use instances if available, fallback to deprecated field)
  const totalQuantity = getTotalInstanceQuantity(item);
  if (totalQuantity < 1) {
    return 'error';
  }

  return 'valid';
}

/**
 * Get total quantity across all instances.
 * Falls back to deprecated quantity field if no instances.
 * @param item - The draft item
 * @returns Total quantity
 */
export function getTotalInstanceQuantity(item: BatchDraftItem): number {
  if (item.instances && item.instances.length > 0) {
    return item.instances.reduce((sum, instance) => sum + instance.quantity, 0);
  }
  // Fallback to deprecated field for v1 items
  return item.quantity;
}

/**
 * Generate a grouping key for matching products.
 * Used to determine if two items can be merged.
 * @param item - The draft item
 * @returns A unique key for the product
 */
export function getProductGroupKey(item: BatchDraftItem): string {
  if (item.productId) {
    return `product:${item.productId}`;
  }
  if (item.barcode) {
    return `barcode:${item.barcode}`;
  }
  if (item.customName) {
    return `custom:${item.customName}`;
  }
  return `client:${item.clientId}`;
}

/**
 * Check if two items can be merged (same product, same unit).
 * @param a - First item
 * @param b - Second item
 * @returns Whether the items can be merged
 */
export function canMergeItems(a: BatchDraftItem, b: BatchDraftItem): boolean {
  // Must have same unit
  if (a.unit !== b.unit) {
    return false;
  }

  // Must be the same product
  return getProductGroupKey(a) === getProductGroupKey(b);
}

/**
 * Extract instances from a source item for merging into a target item.
 * If source has multiple instances, returns them all to preserve per-instance data.
 * If source has a single instance, applies any updates from the edit sheet.
 *
 * @param sourceItem - The item being merged away
 * @param updates - Optional updates from the edit sheet (quantity, expiryDate, notes, purchaseDate)
 * @returns Array of instances to add to the target item
 */
export function extractInstancesForMerge(
  sourceItem: BatchDraftItem,
  updates?: { quantity?: number; expiryDate?: string; notes?: string | null; purchaseDate?: string }
): Omit<BatchDraftInstance, 'instanceId'>[] {
  const hasMultipleInstances = sourceItem.instances && sourceItem.instances.length > 1;

  if (hasMultipleInstances) {
    // Multiple instances - preserve each one individually
    return sourceItem.instances.map((instance) => ({
      quantity: instance.quantity,
      expiryDate: instance.expiryDate,
      purchaseDate: instance.purchaseDate,
      notes: instance.notes,
      addedAt: instance.addedAt ?? Date.now(),
    }));
  }

  // Single instance - use updates or fall back to item-level values
  return [
    {
      quantity: updates?.quantity ?? sourceItem.quantity ?? 1,
      expiryDate: updates?.expiryDate ?? sourceItem.expiryDate,
      purchaseDate: updates?.purchaseDate ?? sourceItem.purchaseDate,
      notes: updates?.notes ?? sourceItem.notes,
      addedAt: Date.now(),
    },
  ];
}

/**
 * Migrate a v1 BatchDraftItem to v2 format.
 * Converts top-level quantity/expiry/notes to instances array.
 * @param v1Item - The v1 item to migrate
 * @param generateId - Function to generate instance IDs (default: returns 'migrated-{clientId}')
 * @returns The migrated v2 item
 */
export function migrateBatchDraftItemV1ToV2(
  v1Item: BatchDraftItemV1,
  generateId: () => string = () => `migrated-${v1Item.clientId}`
): BatchDraftItem {
  return {
    clientId: v1Item.clientId,
    productId: v1Item.productId,
    barcode: v1Item.barcode,
    customName: v1Item.customName,
    displayName: v1Item.displayName,
    displayBrand: v1Item.displayBrand,
    displayImageUrl: v1Item.displayImageUrl,
    isExternal: v1Item.isExternal,
    unit: v1Item.unit,
    storageLocationId: v1Item.storageLocationId,
    addedAt: v1Item.addedAt,
    // Convert to instances array
    instances: [
      {
        instanceId: generateId(),
        quantity: v1Item.quantity,
        expiryDate: v1Item.expiryDate,
        notes: v1Item.notes,
        addedAt: v1Item.addedAt,
      },
    ],
    // Keep deprecated fields for backwards compatibility
    quantity: v1Item.quantity,
    expiryDate: v1Item.expiryDate,
    notes: v1Item.notes,
  };
}

/**
 * Check if a BatchDraftItem is in v1 format (no instances array).
 * @param item - The item to check
 * @returns True if the item is v1 format
 */
export function isBatchDraftItemV1(item: unknown): item is BatchDraftItemV1 {
  const obj = item as Record<string, unknown>;
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'clientId' in obj &&
    !('instances' in obj && Array.isArray(obj.instances))
  );
}

/**
 * Storage key constants for draft persistence.
 */
export const BATCH_DRAFT_STORAGE_KEY = 'pantry_batch_draft';

/**
 * Default expiration time for draft (24 hours in milliseconds).
 */
export const BATCH_DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000;
